import ClassCard from 'aquilla/core/organisms/ClassCard';
import styled from '@emotion/styled';
import EducatorThumbnailBackground from 'aquilla/core/atoms/EducatorThumbnailBackground';
import Image from '@base/Image';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import MUIDialog from '@material-ui/core/Dialog';
import YouTube from '@cont/FreeLesson/externalyoutube';
import { useEffect, useState } from 'react';
import { segment } from '@actions/analytics';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { useRouter } from 'next/router';
import { CENTRE_DEMO_VIDEO_PLAYED, OFFLINE_CENTRE_SOURCE } from './constants';
import GridContainer from './CommonComponents';

const StyGridContainer = styled(GridContainer)`
  margin-bottom: ${Spacings.SPACING_16B};
  margin-top: ${Spacings.SPACING_16B};
  ${mediaQuery.uptoMobile} {
    padding-right: 0px !important;
    padding-left: 0px !important;
    margin-bottom: auto;
    margin-top: auto;
  }
`;

const DemoClassesCarouselWrapper = styled.div`
  grid-column: span 24;
  width: 100%;
`;

const EduThumbWrapper = styled.div`
  position: relative;
`;

const EduImageWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 50%;
  transform: translateX(-50%);
`;
const EduImage = styled(Image)``;

const VideoCardContainer = styled.div`
  position: relative;
`;

const StyledDialog = styled(MUIDialog)`
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    position: absolute;
    background: var(--color-text-primary);
    height: 80%;
    width: 88%;
    max-width: 88%;

    ${mediaQuery.uptoMobile} {
      margin: ${Spacings.SPACING_4B} auto;
      height: 30%;
    }
  }
`;

const StyShowCase = styled(ShowCase)`
  & .aquilla-showcase-section-header-heading {
    width: 100%;
    & .aquilla-typography {
      font-size: ${Fonts.H3};
      line-height: ${Spacings.SPACING_8B};
      ${mediaQuery.uptoMobile} {
        font-size: ${Fonts.H4};
        line-height: ${Spacings.SPACING_26};
      }
    }
  }
`;

const ImageComp = ({ url, width, height, color }) => {
  return (
    <EduThumbWrapper>
      <EducatorThumbnailBackground variant="content-thumbnail" color={color} />
      <EduImageWrapper>
        <EduImage src={url} width={width} height={height} alt="edu-image" />
      </EduImageWrapper>
    </EduThumbWrapper>
  );
};

const opts = {
  height: '100%',
  width: '100%',
  playerVars: {
    autoplay: 1,
    showinfo: 0,
    rel: 0
  }
};

const VideoCard = ({ videoUrl, isOpen, closePlayer }) => {
  return (
    <VideoCardContainer>
      <StyledDialog open={isOpen} onClose={closePlayer}>
        <YouTube
          opts={opts}
          videoId={videoUrl}
          onEnd={closePlayer}
          useWrapper={false}
        />
      </StyledDialog>
    </VideoCardContainer>
  );
};

const DemoClasses = ({ data }) => {
  const { headerWeb: header, classes } = data;
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const [isOpen, setIsOpen] = useState(false);
  const [playerData, setPlayerData] = useState({});
  const closePlayer = () => setIsOpen(false);
  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();
  const { centreInfo } = useCentreInfo(routeGoalUID, centreUID);
  const { centreName, cityName } = centreInfo || {};

  const { isDemoClassesClicked, setDemoClassesClicked } =
    useOfflineCentreStore();

  useEffect(() => {
    if (isLoggedIn) {
      if (isDemoClassesClicked) {
        setIsOpen(true);
        setDemoClassesClicked(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const renderDemoClasses = () => {
    const cards = classes.map((demoClass) => {
      const {
        subject: topicName,
        color: subjectColor,
        educatorName,
        languageTag,
        thumbnailWeb,
        classTitle,
        videoUid
      } = demoClass;
      const NewImageComp = () => (
        <ImageComp
          url={thumbnailWeb}
          width={128}
          height={156}
          color={subjectColor}
        />
      );
      const onClassClick = () => {
        if (!isLoggedIn) {
          // if not logged in - open login drawer
          setDemoClassesClicked(true);
          setPlayerData({ videoUrl: videoUid });
          openLoginDrawer();
          return;
        }
        setIsOpen(true);
        setPlayerData({ videoUrl: videoUid });
        segment.track(CENTRE_DEMO_VIDEO_PLAYED, {
          centre_name: centreName,
          city: cityName,
          goal_uid: routeGoalUID,
          demovideo_source: OFFLINE_CENTRE_SOURCE
        });
      };
      return (
        <ClassCard
          variant="Continue Watching"
          title={classTitle}
          langLabel={languageTag}
          topicName={topicName}
          key={classTitle}
          educatorName={educatorName}
          thumbnail={NewImageComp}
          topicColor={subjectColor}
          onClick={onClassClick}
        />
      );
    });
    return (
      <>
        <StyShowCase title={header} itemsPerSlide={3}>
          {cards}
        </StyShowCase>
        <VideoCard
          videoUrl={playerData.videoUrl}
          isOpen={isOpen}
          closePlayer={closePlayer}
        />
      </>
    );
  };

  return (
    <StyGridContainer>
      <DemoClassesCarouselWrapper>
        {renderDemoClasses()}
      </DemoClassesCarouselWrapper>
    </StyGridContainer>
  );
};

export default DemoClasses;
